/**
 * transitions.ts — order status transitions and role key resolution.
 *
 * Ported from frontend-bk/erp/src/hooks/useOrderTransitions.js.
 * Pure functions only — no React state. Components use these directly
 * or via TanStack Query mutations.
 */
import { isIdPhotoOrder } from "@/features/proofs/utils";
import { isShiftLeadTitle } from "@/lib/auth/page-access";

/** Hợp đồng báo giá lúc mới tạo mang trạng thái này (máy chủ ép, QĐ 0178) —
 *  chốt / huỷ theo đúng lối của đơn dự bị. Khớp `order.QuoteStatusHolding`. */
export const QUOTE_HOLDING = "giữ chỗ tạm";

/**
 * Role-based allowed status transitions.
 * Key: role string returned by getRoleKey().
 * Value: map of current status → allowed next statuses[].
 * Empty object = role can VIEW orders but cannot change any status.
 */
export const ROLE_TRANSITIONS: Record<string, Record<string, string[]>> = {
  admin: {
    tentative: ["booked", "cancelled"], // Chốt đơn dự bị → booked / Hủy → cancelled
    [QUOTE_HOLDING]: ["booked", "cancelled"],
    booked: ["shooting", "cancelled"],
    contract: ["shooting", "cancelled"],
    shooting: ["editing"],
    editing: ["completed"],
    completed: ["delivered", "editing"],
    delivered: ["editing"],
  },
  director: {
    tentative: ["booked", "cancelled"],
    [QUOTE_HOLDING]: ["booked", "cancelled"],
    booked: ["shooting", "cancelled"],
    contract: ["shooting", "cancelled"],
    shooting: ["editing"],
    editing: ["completed"],
    completed: ["delivered", "editing"],
    delivered: ["editing"],
  },
  // Vai `coordinator` (Điều Phối) ĐÃ KHOÁ 23/08/2026 (quyết định 0050): khối
  // của nó bị gỡ hẳn khỏi bảng, nên `ROLE_TRANSITIONS["coordinator"]?.[status]
  // ?? []` cho ra [] — không còn chuyển được trạng thái nào. Trưởng cửa hàng
  // bên dưới tiếp quản.
  // Trưởng cửa hàng điều hành trọn vẹn cơ sở — sao y ban điều hành (spec
  // 2026-08-23 §5.7; BE order.RoleTransitions giữ bản sao y hệt).
  branch_manager: {
    tentative: ["booked", "cancelled"],
    [QUOTE_HOLDING]: ["booked", "cancelled"],
    booked: ["shooting", "cancelled"],
    contract: ["shooting", "cancelled"],
    shooting: ["editing"],
    editing: ["completed"],
    completed: ["delivered", "editing"],
    delivered: ["editing"],
  },
  sale: {
    tentative: ["booked", "cancelled"],
    [QUOTE_HOLDING]: ["booked", "cancelled"],
    booked: ["shooting", "cancelled"],
    contract: ["shooting", "cancelled"],
  },
  reception: {
    tentative: ["booked", "cancelled"], // confirm/cancel a reserve slot
    [QUOTE_HOLDING]: ["booked", "cancelled"],
    booked: ["shooting", "cancelled"], // can cancel a booked order
  },
  photo: { shooting: ["editing"] },
  // Makeup finishes their work during the shoot, so they can advance shooting → editing.
  makeup: { shooting: ["editing"] },
  retouch: { editing: ["completed"] },
  cskh: { completed: ["delivered", "editing"], delivered: ["editing"] },
  accounting: {}, // can view all orders but cannot change any status
  marketing: {}, // can view orders but cannot change any status
};

export interface UserForTransitions {
  role?: string | null;
  department?: string | null;
  /** Chức danh HRM (QĐ 0425): `truong_ca` nhận khoá Trưởng cửa hàng. */
  jobTitleCode?: string | null;
}

/**
 * Map a user object to a role key used in ROLE_TRANSITIONS.
 * Priority: explicit role field → department field → fallback "staff".
 *
 * GƯƠNG của `RoleKey` ở máy chủ (`backend/internal/core/order/transitions.go`).
 * Lối cuối là "staff" — khoá KHÔNG có trong bảng ⇒ không nút nào. Trước
 * 28/09/2026 lối cuối là "admin": vai lạ (viewer, area_manager, staff không
 * khai phòng…) và lúc CHƯA nạp xong người dùng được bày ĐỦ nút, bấm vào máy
 * chủ chối 403 — đúng kiểu nút chết mà bài gương bên cạnh canh.
 */
export function getRoleKey(user: UserForTransitions | null | undefined): string {
  if (!user) return "staff";
  // Trưởng ca (QĐ 0425): bảng Trưởng cửa hàng — rộng hơn bảng phòng mình nên
  // không mất nút nào của việc chính. Gương `gateAllows` / `ActorOpsRole`.
  if (isShiftLeadTitle(user)) return "branch_manager";
  if (
    user.role === "admin" ||
    user.role === "director" ||
    user.role === "coordinator" ||
    user.role === "branch_manager"
  )
    return user.role;
  // Trưởng cửa hàng có khoá RIÊNG, không gộp vào admin: máy chủ cắt tầm nhìn
  // của vai này về đúng cơ sở của họ, nên gộp là nói dối về phạm vi.
  if (user.role === "branch_manager") return "branch_manager";
  // manager không có bảng riêng — máy chủ ghi rõ nhánh này rơi về admin.
  // QC = quyền leader phòng mình (spec 2026-08-23 §5.6) — máy chủ cho cùng khoá.
  if (user.role === "manager" || user.role === "qc") return "admin";
  // Kế toán / marketing tồn tại ở CẢ HAI tầng (chức danh và phòng ban). Thiếu
  // tầng chức danh thì một kế toán không khai phòng ban sẽ rơi về "admin" và
  // giao diện bày ra đủ nút — bấm vào máy chủ chối, người dùng chỉ thấy nút chết.
  if (user.role === "accounting") return "accounting";
  if (user.role === "marketing") return "marketing";
  // Vai chỉ xem, Trưởng ca (đã khoá — QĐ 0228) và quản lý cụm KHÔNG mượn khoá
  // của phòng họ mang — gương `RoleKey` máy chủ (kiểm thử đóng vai 17/10/2026,
  // Q01: Chỉ xem phòng Lễ tân thấy nút Huỷ đơn và huỷ được). Điều phối đã trả
  // khoá riêng ở trên (không có bảng ⇒ không nút).
  if (user.role === "viewer" || user.role === "shift_lead" || user.role === "area_manager")
    return "staff";
  const dept = user.department;
  if (dept === "sale" || dept === "sales") return "sale";
  if (dept === "reception") return "reception";
  if (dept?.startsWith("photo")) return "photo";
  if (dept === "makeup") return "makeup";
  if (dept === "retouch") return "retouch";
  if (dept === "cskh") return "cskh";
  if (dept === "accounting") return "accounting";
  if (dept === "marketing") return "marketing";
  return "staff";
}

/**
 * Vai được bấm "Khách đã tới" (spec 2026-08-23 T2, Đ2/Đ12).
 *
 * Đọc theo KẾT QUẢ của máy chủ, KHÔNG chép nguyên bảng `arrivedRoles` bên đó:
 * bảng ấy còn dòng "coordinator" đã chết, vì `RoleKey` của máy chủ đưa vai Điều
 * phối (khoá 23/08/2026, quyết định 0050) về "staff" nên không bao giờ khớp.
 * `getRoleKey` bên này thì vẫn trả "coordinator" — chép y bảng là bày ra một nút
 * bấm vào ăn 403.
 */
const ARRIVED_ROLE_KEYS = new Set(["reception", "admin", "director", "branch_manager"]);

export function canPressCustomerArrived(user: UserForTransitions | null | undefined): boolean {
  return ARRIVED_ROLE_KEYS.has(getRoleKey(user));
}

export interface OrderForTransitions {
  category?: string | null;
  product?: string | null;
  productId?: string | null;
}

/**
 * Return the list of statuses this user can transition an order to
 * from its current status.
 *
 * `order` is optional and only widens the result in one case: on an ảnh-thẻ
 * order the photographer may close completed → delivered, because they are
 * the one handing the prints over (CEO 17/08/2026). The server checks two
 * more things this cannot see — that the photographer actually worked that
 * shift, and that a hand-over photo exists.
 */
export function getTransitionsForUser(
  user: UserForTransitions | null | undefined,
  currentStatus: string,
  order?: OrderForTransitions,
): string[] {
  const key = getRoleKey(user);
  const base = ROLE_TRANSITIONS[key]?.[currentStatus] ?? [];
  if (
    key === "photo" &&
    currentStatus === "completed" &&
    order &&
    isIdPhotoOrder(order) &&
    !base.includes("delivered")
  ) {
    return [...base, "delivered"];
  }
  return base;
}
